import React, { useState, useEffect } from "react";
import { activitiesAPI, clientsAPI, dealsAPI, contactsAPI } from "../../utils/api";
import { formatApiError } from "../../utils/errorFormatter";
import { X, Loader2 } from "lucide-react";

const ActivityModal = ({ isOpen, onClose, activity = null, onSuccess, preSelectedDealId = null, preSelectedClientId = null }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [clients, setClients] = useState([]);
  const [deals, setDeals] = useState([]);
  const [contacts, setContacts] = useState([]);
  
  const [formData, setFormData] = useState({
    deal_id: preSelectedDealId || "",
    client_id: preSelectedClientId || "",
    contact_id: "",
    activity_type: "Meeting",
    subject: "",
    date: new Date().toISOString().split("T")[0],
    notes: "",
    outcome: "Pending",
    next_action: "",
    next_action_date: ""
  });

  useEffect(() => {
    if (isOpen) {
      fetchDropdownData();
      if (activity) {
        setFormData({
          deal_id: activity.deal_id || "",
          client_id: activity.client_id || "",
          contact_id: activity.contact_id || "",
          activity_type: activity.activity_type || "Meeting",
          subject: activity.subject || "",
          date: activity.date ? activity.date.split("T")[0] : new Date().toISOString().split("T")[0],
          notes: activity.notes || "",
          outcome: activity.outcome || "Pending",
          next_action: activity.next_action || "",
          next_action_date: activity.next_action_date ? activity.next_action_date.split("T")[0] : ""
        });
      } else {
        if (preSelectedDealId) setFormData(prev => ({ ...prev, deal_id: preSelectedDealId }));
        if (preSelectedClientId) setFormData(prev => ({ ...prev, client_id: preSelectedClientId }));
      }
    }
  }, [isOpen, activity, preSelectedDealId, preSelectedClientId]);

  const fetchDropdownData = async () => {
    try {
      const [clientsRes, dealsRes] = await Promise.all([
        clientsAPI.getAll(),
        dealsAPI.getAll()
      ]);
      setClients(clientsRes.data);
      setDeals(dealsRes.data);
    } catch (err) {
      console.error("Error fetching dropdown data:", err);
    }
  };

  useEffect(() => {
    if (formData.client_id) {
      contactsAPI.getAll({ client_id: formData.client_id })
        .then(res => setContacts(res.data))
        .catch(err => console.error("Error fetching contacts:", err));
    }
  }, [formData.client_id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (!formData.client_id) throw new Error("Client is required");
      if (!formData.subject) throw new Error("Subject is required");

      const payload = {
        ...formData,
        date: new Date(formData.date).toISOString(),
        next_action_date: formData.next_action_date ? new Date(formData.next_action_date).toISOString() : null
      };

      if (activity) {
        await activitiesAPI.update(activity._id, payload);
      } else {
        await activitiesAPI.create(payload);
      }

      onSuccess?.();
      onClose();
    } catch (err) {
      setError(formatApiError(err, "Failed to save activity"));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" 
      onClick={onClose}
      data-testid="activity-modal-overlay"
    >
      <div 
        className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto" 
        onClick={(e) => e.stopPropagation()}
        data-testid="activity-modal"
      >
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-semibold text-slate-900">
            {activity ? "Edit Activity" : "Log New Activity"}
          </h2>
          <button 
            type="button"
            onClick={onClose} 
            className="text-slate-400 hover:text-slate-600 p-1 rounded-full hover:bg-slate-100 transition-colors" 
            data-testid="activity-modal-close"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4" data-testid="activity-form">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="activity-form-error">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Activity Type *</label>
              <select
                name="activity_type"
                value={formData.activity_type}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="activity-type-select"
              >
                <option value="Call">Call</option>
                <option value="Meeting">Meeting</option>
                <option value="Email">Email</option>
                <option value="Proposal">Proposal</option>
                <option value="Site Visit">Site Visit</option>
                <option value="Presentation">Presentation</option>
                <option value="Follow-up">Follow-up</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Date *</label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                required
                data-testid="activity-date-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Client *</label>
              <select
                name="client_id"
                value={formData.client_id}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                required
                disabled={!!preSelectedClientId}
                data-testid="activity-client-select"
              >
                <option value="">Select Client</option>
                {clients.map(c => (
                  <option key={c._id} value={c._id}>{c.client_name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Deal (Optional)</label>
              <select
                name="deal_id"
                value={formData.deal_id}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                disabled={!!preSelectedDealId}
                data-testid="activity-deal-select"
              >
                <option value="">Select Deal</option>
                {deals.map(d => (
                  <option key={d._id} value={d._id}>{d.project_title}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Contact</label>
              <select
                name="contact_id"
                value={formData.contact_id}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                disabled={!formData.client_id}
                data-testid="activity-contact-select"
              >
                <option value="">Select Contact</option>
                {contacts.map(c => (
                  <option key={c._id} value={c._id}>{c.full_name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Outcome</label>
              <select
                name="outcome"
                value={formData.outcome}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="activity-outcome-select"
              >
                <option value="Positive">Positive</option>
                <option value="Neutral">Neutral</option>
                <option value="Negative">Negative</option>
                <option value="Pending">Pending</option>
              </select>
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Subject *</label>
              <input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                placeholder="Brief description of the activity"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                required
                data-testid="activity-subject-input"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows="4"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="activity-notes-textarea"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Next Action</label>
              <input
                type="text"
                name="next_action"
                value={formData.next_action}
                onChange={handleChange}
                placeholder="What needs to be done next?"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="activity-next-action-input"
              />
            </div>

            {formData.next_action && (
              <div className="col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">Next Action Date</label>
                <input
                  type="date"
                  name="next_action_date"
                  value={formData.next_action_date}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="activity-next-action-date-input"
                />
              </div>
            )}
          </div>

          <div className="flex gap-3 justify-end pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors"
              data-testid="activity-cancel-btn"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              data-testid="activity-submit-btn"
            >
              {loading && <Loader2 className="animate-spin" size={16} />}
              {activity ? "Update Activity" : "Log Activity"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ActivityModal;
